import axios, { AxiosInstance, AxiosResponse } from "axios";
import _ from "underscore";

import { API_CONFIG, API_ERROR_MESSAGE, ServiceMenuType } from "@/consts/const";
import { ApiNoNetworkError } from "@/errors/api/api-no-network-error";
import { ApiResponseError } from "@/errors/api/api-response-error";
import { ApiResponseNotFoundError } from "@/errors/api/api-response-not-found-error";
import { ApiTimeoutError } from "@/errors/api/api-timeout-error";
import { ServiceMenuAndEventsResponseInterface } from "@/interfaces/api/service-menu-and-events-response.interface";
import { ServiceMenuAndOneEventResponseInterface } from "@/interfaces/api/service-menu-and-one-event-response.interface";
import { ImportantMessageInterface } from "@/interfaces/important-message.interface";
import { ServiceInterface } from "@/interfaces/service.interface";
import { ServiceCategoryInterface } from "@/interfaces/service-category.interface";
import { ServiceGroupInterface } from "@/interfaces/service-group.interface";
import { ServiceMenuInterface } from "@/interfaces/service-menu.interface";
import { EventUtil } from "@/utils/event.util";
import { StatusUtil } from "@/utils/status-util";
import i18n from "@/main";

// タイムアウト時のエラーコード: axiosに関連する値のためここで定義（変更不可）
const TIMEOUT_ERROR_CODE = "ECONNABORTED";

/**
 * APIクラス
 */
export class ApiService {
  private readonly client: AxiosInstance;

  /**
   * コンストラクタ
   */
  constructor() {
    this.client = axios.create({
      baseURL: `${API_CONFIG.BASE_URL}/api`,
      timeout: API_CONFIG.TIMEOUT,
      // withCredentials: true,
      // encoding: 'UTF-8'は必須項目
      data: {
        encoding: API_CONFIG.ENCODING,
      },
      headers: {
        "Content-Type": API_CONFIG.CONTENT_TYPE,
      },
    });
  }

  /**
   * サービスカテゴリ一覧を取得する
   */
  getServiceCategories(): Promise<ServiceCategoryInterface[]> {
    return this.httpGet<ServiceCategoryInterface[]>("/category").then(
      (items: ServiceCategoryInterface[]) => {
        return items.map((item: ServiceCategoryInterface) =>
          _.extend({ serviceGroups: [] }, item)
        );
      }
    );
  }

  /**
   * サービスカテゴリ内のサービス情報を取得する
   */
  getServicesInServiceCategory(
    serviceCategoryId: number
  ): Promise<ServiceGroupInterface[]> {
    return this.httpGet<ServiceGroupInterface[]>(
      `/category/${serviceCategoryId}`
    ).then((groups: ServiceGroupInterface[]) => {
      return groups.map((group: ServiceGroupInterface) => {
        group.services.forEach((service: ServiceInterface) => {
          service.serviceMenus.forEach((serviceMenu: ServiceMenuInterface) => {
            if (serviceMenu.type === ServiceMenuType.PortalSite) {
              return;
            }

            serviceMenu.events = EventUtil.sort(serviceMenu.events);
            serviceMenu.data = StatusUtil.getEventStatusData(
              serviceMenu.events
            );
          });
        });

        return group;
      });
    });
  }

  /**
   * サービスメニューとイベント一覧を取得する
   */
  getServiceMenuAndEvents(
    serviceMenuId: number
  ): Promise<ServiceMenuAndEventsResponseInterface> {
    return this.httpGet<ServiceMenuAndEventsResponseInterface>(
      `/service-menu/${serviceMenuId}/events`
    ).then((data: ServiceMenuAndEventsResponseInterface) => {
      data.events = EventUtil.sort(data.events);
      return data;
    });
  }

  /**
   * サービスメニューとイベントを取得する
   */
  getServiceMenuAndOneEvent(
    eventId: string
  ): Promise<ServiceMenuAndOneEventResponseInterface> {
    return this.httpGet(`/service-menu-event/${eventId}`);
  }

  /**
   * 重要なお知らせを取得する
   */
  getImportantMessages(): Promise<ImportantMessageInterface[]> {
    return this.httpGet(`/important-message`);
  }

  /**
   * 過去の重要なお知らせを取得する
   */
   getImportantOld(): Promise<ImportantMessageInterface[]> {
    return this.httpGet(`/important-past`);
  }


  /**
   * GETで値を送信する
   */
  private async httpGet<T>(path: string, params: unknown = {}): Promise<T> {
    return this.execute<T>((): Promise<AxiosResponse<T>> => {
      return this.client.get(path, {
        params: params,
      });
    });
  }

  /**
   * HTTP通信を実行する
   */
  private async execute<T>(
    callback: () => Promise<AxiosResponse<T>>
  ): Promise<T> {
    try {
      const res: AxiosResponse<T> = await callback();
      return res.data;
    } catch (e) {
      if (e.code === TIMEOUT_ERROR_CODE) {
        throw new ApiTimeoutError(i18n.t(API_ERROR_MESSAGE.TIMEOUT));
      }

      if (_.isUndefined(e.response)) {
        throw new ApiNoNetworkError(i18n.t(API_ERROR_MESSAGE.NETWORK));
      }

      const statusCode = e.response.status;
      const data = e.response.data;

      if (!data) {
        throw this.createResponseError(statusCode, i18n.t(API_ERROR_MESSAGE.UNKNOWN));
      } else {
        throw this.createResponseError(statusCode, data.message, data.data);
      }
    }
  }

  private createResponseError(
    statusCode: number,
    message: string,
    data: unknown = null
  ): ApiResponseError {
    if (statusCode === 404) {
      return new ApiResponseNotFoundError(message, data);
    }

    return new ApiResponseError(statusCode, message, data);
  }
}
